// WCAG AA contrast helpers; mirrors the widget's app.helper.ts/widgetTheme.helper.ts (separate codebases).
const HEX_RE = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;
const RGB_RE = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/;

export const WCAG_AA_NORMAL = 4.5;

const parseColor = (input: string): [number, number, number] | null => {
  if (HEX_RE.test(input)) {
    const hex = input.replace('#', '');
    const expanded =
      hex.length === 3
        ? hex
            .split('')
            .map((c) => c + c)
            .join('')
        : hex.slice(0, 6);
    const n = parseInt(expanded, 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  }
  const m = input.match(RGB_RE);
  if (m) return [Number(m[1]), Number(m[2]), Number(m[3])];
  return null;
};

const luminance = ([r, g, b]: [number, number, number]): number => {
  const a = [r, g, b].map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * a[0] + 0.7152 * a[1] + 0.0722 * a[2];
};

export const getContrastRatio = (fg: string, bg: string): number | null => {
  const fgRgb = parseColor(fg);
  const bgRgb = parseColor(bg);
  if (!fgRgb || !bgRgb) return null;
  const L1 = luminance(fgRgb);
  const L2 = luminance(bgRgb);
  const bright = Math.max(L1, L2);
  const dark = Math.min(L1, L2);
  return (bright + 0.05) / (dark + 0.05);
};

// Black or white, whichever reads better on `bg` (black/white luminance is 0/1).
export const getAccessibleTextColor = (bg: string): string => {
  const rgb = parseColor(bg);
  if (!rgb) return '#000000';
  const L = luminance(rgb);
  const blackContrast = (L + 0.05) / 0.05;
  const whiteContrast = 1.05 / (L + 0.05);
  if (blackContrast >= WCAG_AA_NORMAL) return '#000000';
  if (whiteContrast >= WCAG_AA_NORMAL) return '#ffffff';
  return blackContrast > whiteContrast ? '#000000' : '#ffffff';
};
